/* BEAGLETUFF HERBST-DEKO – Windstoß mit Blättern und wachsende Pilze (snippets/bt-herbst.liquid) */
(() => {
  const config = window.btHerbst;
  if (!config) return;

  const SESSION_KEY = 'bt-herbst-windstoss';
  const START_DELAY_MS = 700;
  const SPAWN_WINDOW_MS = 1100;
  const MAX_LEAF_LIFETIME_S = 9;
  const IMAGE_TIMEOUT_MS = 4000;
  const SECRET_LEAF_COUNT = 5;
  const WIGGLE_MS = 900;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const random = (min, max) => min + Math.random() * (max - min);

  const berlinCalendar = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
  });

  function berlinDate(now = new Date()) {
    const parts = Object.fromEntries(berlinCalendar.formatToParts(now).map(({ type, value }) => [type, value]));
    return `${parts.year}-${parts.month}-${parts.day}`;
  }

  function isExpired(today = berlinDate()) {
    const end = /^\d{4}-\d{2}-\d{2}$/.test(config.ende || '') ? config.ende : '2026-11-15';
    return today >= end || today >= '2026-11-15';
  }

  function seasonFor(today) {
    if (isExpired(today)) return 'aus';
    if (today >= '2026-10-27' && today < '2026-11-01') return 'halloween';
    if (today >= '2026-10-15' && today < '2026-10-27') return 'kuerbisse';
    return 'herbst';
  }

  function readSession() {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function writeSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (error) {
      /* privater Modus: dann läuft der Windstoß eben erneut */
    }
  }

  function shouldBlow() {
    if (!config.wind || reducedMotion) return false;
    if (config.editor || new URLSearchParams(location.search).get('herbst') === 'neu') return true;
    return !readSession();
  }

  function loadImages(urls) {
    const loads = urls.map((url) => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    }));
    const timeout = new Promise((resolve) => setTimeout(() => resolve([]), IMAGE_TIMEOUT_MS));
    return Promise.race([Promise.all(loads), timeout]).then((imgs) => imgs.filter(Boolean));
  }

  /* Startwerte: Windstoß von links, oder (Pilz-Geheimnis) Blätter wirbeln am Pilz hoch. */
  function startMotion(depth, viewport, origin) {
    if (!origin) {
      return {
        delay: random(0, SPAWN_WINDOW_MS) / 1000,
        x: random(-160, -60),
        y: random(0.02, 0.6) * viewport.height,
        vx: random(700, 1250) * depth,
        vy: random(-90, 20),
        breeze: random(25, 70) * depth,
      };
    }
    const direction = origin.x < viewport.width / 2 ? 1 : -1;
    return {
      delay: random(0, 350) / 1000,
      x: origin.x + random(-25, 25),
      y: origin.y + random(-15, 15),
      vx: random(90, 320) * direction,
      vy: random(-480, -260),
      breeze: random(15, 55) * direction,
    };
  }

  /* Ein Blatt: wird vom Wind getragen, gebremst und trudelt pendelnd nach unten. */
  function createLeaf(img, layer, viewport, origin) {
    const depth = random(0.45, 1.1);
    const baseSize = viewport.width < 700 ? 46 : 64;
    const el = document.createElement('img');
    el.src = img.src;
    el.alt = '';
    el.className = 'bt-herbst-blatt';
    if (depth > 0.98) el.classList.add('bt-herbst-blatt--nah');
    if (depth < 0.6) el.classList.add('bt-herbst-blatt--fern');
    el.style.setProperty('--w', `${Math.round(baseSize * depth)}px`);
    el.style.opacity = '0';
    layer.appendChild(el);

    return {
      el,
      depth,
      ...startMotion(depth, viewport, origin),
      drag: random(1.1, 1.6),
      fallSpeed: random(95, 170) * depth,
      swayAmp: random(18, 46) * depth,
      swayFreq: random(0.55, 1.1),
      phase: random(0, Math.PI * 2),
      rotZ: random(-40, 40),
      spin: random(160, 420) * (Math.random() < 0.5 ? -1 : 1),
      tiltX: random(35, 70),
      tiltY: random(20, 45),
      opacity: depth < 0.6 ? 0.8 : 1,
      done: false,
    };
  }

  function stepLeaf(leaf, t, dt, viewport) {
    const age = t - leaf.delay;
    if (age < 0) return;

    leaf.vx += (leaf.breeze - leaf.vx) * Math.min(1, leaf.drag * dt);
    leaf.vy += (leaf.fallSpeed - leaf.vy) * Math.min(1, 0.9 * dt);
    leaf.spin *= 1 - Math.min(1, 0.55 * dt);

    const sway = Math.sin(age * leaf.swayFreq * Math.PI * 2 + leaf.phase);
    leaf.x += leaf.vx * dt;
    leaf.y += leaf.vy * dt;
    leaf.rotZ += leaf.spin * dt + sway * 30 * dt;

    const x = leaf.x + sway * leaf.swayAmp;
    const rotX = Math.sin(age * leaf.swayFreq * Math.PI * 2 + leaf.phase + 0.8) * leaf.tiltX;
    const rotY = Math.cos(age * leaf.swayFreq * Math.PI + leaf.phase) * leaf.tiltY;
    const fadeIn = Math.min(1, age * 4);
    const fadeOut = age > MAX_LEAF_LIFETIME_S - 1 ? Math.max(0, MAX_LEAF_LIFETIME_S - age) : 1;

    leaf.el.style.opacity = String(leaf.opacity * fadeIn * fadeOut);
    leaf.el.style.transform =
      `translate3d(${x.toFixed(1)}px, ${leaf.y.toFixed(1)}px, 0) ` +
      `perspective(400px) rotateZ(${leaf.rotZ.toFixed(1)}deg) rotateX(${rotX.toFixed(1)}deg) rotateY(${rotY.toFixed(1)}deg)`;

    const outside = leaf.y > viewport.height + 80 || x > viewport.width + 160 || x < -200;
    if (outside || age > MAX_LEAF_LIFETIME_S) leaf.done = true;
  }

  let activeLayer = null;

  function blow(images, origin = null, count = Number(config.anzahl) || 7) {
    if (!config.wind || reducedMotion || isExpired() || !images.length || activeLayer) return;
    if (!origin) writeSession();

    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const layer = document.createElement('div');
    layer.className = 'bt-herbst-wind';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
    activeLayer = layer;

    const leaves = Array.from({ length: Math.max(1, count) }, (_, i) =>
      createLeaf(images[i % images.length], layer, viewport, origin));

    let start = null;
    let last = null;
    const frame = (now) => {
      if (!layer.isConnected || isExpired()) {
        layer.remove();
        activeLayer = null;
        return;
      }
      if (start === null) start = last = now;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = (now - start) / 1000;

      leaves.forEach((leaf) => leaf.done || stepLeaf(leaf, t, dt, viewport));
      if (leaves.every((leaf) => leaf.done)) {
        layer.remove();
        activeLayer = null;
        return;
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  function shuffle(list) {
    return list
      .map((item) => ({ item, key: Math.random() }))
      .sort((a, b) => a.key - b.key)
      .map(({ item }) => item);
  }

  /* Pilze: an den unteren Rand vom Startbild oder über den Footer setzen, beim Sichtbarwerden wachsen lassen. */
  function placeMushrooms(strip) {
    const main = document.getElementById('main');
    if (!main) return false;

    if (strip.dataset.ort === 'footer') {
      const footer = main.querySelector('.shopify-section-group-footer-group');
      if (!footer) return false;
      footer.before(strip);
      return true;
    }

    const firstSection = main.querySelector('.shopify-section');
    if (!firstSection) return false;
    if (getComputedStyle(firstSection).position === 'static') firstSection.style.position = 'relative';
    strip.classList.add('bt-herbst-pilze--startbild');
    firstSection.appendChild(strip);
    return true;
  }

  function growMushrooms() {
    const strip = document.querySelector('.bt-herbst-pilze');
    if (!strip) return;
    if (!placeMushrooms(strip)) {
      strip.remove();
      return;
    }
    strip.hidden = false;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      strip.classList.add('ist-gewachsen');
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      strip.classList.add('ist-gewachsen');
      observer.disconnect();
    }, { rootMargin: '0px 0px -12% 0px' });
    strip.querySelectorAll('.bt-herbst-pilze__gruppe').forEach((group) => observer.observe(group));
  }

  /* Pilz-Geheimnis: Antippen lässt den Pilz wackeln und ein paar Blätter aufwirbeln. */
  function enableMushroomSecret(images) {
    const strip = document.querySelector('.bt-herbst-pilze');
    if (!strip || reducedMotion) return;

    strip.addEventListener('click', (event) => {
      const mushroom = event.target.closest('.bt-herbst-pilz');
      if (!mushroom || !strip.classList.contains('ist-gewachsen')) return;

      mushroom.classList.remove('ist-wackelnd');
      void mushroom.offsetWidth;
      mushroom.classList.add('ist-wackelnd');
      setTimeout(() => mushroom.classList.remove('ist-wackelnd'), WIGGLE_MS);

      const rect = mushroom.getBoundingClientRect();
      images.then((loaded) => blow(shuffle(loaded), { x: rect.left + rect.width / 2, y: rect.top + rect.height * 0.3 }, SECRET_LEAF_COUNT));
    });
  }

  /* Ergänzt die vorhandenen Pilzgruppen; die Theme-Einstellungen bleiben maßgeblich. */
  function enableSeason(images) {
    const strip = document.querySelector('.bt-herbst-pilze');
    if (!strip || !config.saison) return;
    const left = strip.querySelector('.bt-herbst-pilze__gruppe--links');
    const right = strip.querySelector('.bt-herbst-pilze__gruppe--rechts');
    if (!left || !right) return;
    const german = document.documentElement.lang.toLowerCase().startsWith('de');
    let previousSeason;
    let timer;

    function picture(src, className) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.width = 320;
      img.height = 320;
      img.decoding = 'async';
      img.className = className;
      return img;
    }

    function motif(parent, tag, className, src, label) {
      const element = document.createElement(tag);
      element.className = className;
      element.dataset.btSaison = '';
      if (tag === 'button') {
        element.type = 'button';
        element.setAttribute('aria-label', label);
        element.disabled = reducedMotion;
      }
      element.appendChild(picture(src, 'bt-herbst-saison__bild'));
      parent.appendChild(element);
      return element;
    }

    function syncSeason() {
      const season = seasonFor(berlinDate());
      if (season === previousSeason) return;
      previousSeason = season;
      strip.querySelectorAll('[data-bt-saison]').forEach((element) => element.remove());
      if (season === 'aus') {
        strip.remove();
        activeLayer?.remove();
        clearInterval(timer);
        document.removeEventListener('visibilitychange', syncSeason);
        window.removeEventListener('pageshow', syncSeason);
        return;
      }
      if (season === 'herbst') return;
      motif(right, 'span', 'bt-herbst-kuerbis bt-herbst-kuerbis--butternut', config.saison.butternut);
      if (season === 'kuerbisse') {
        motif(left, 'span', 'bt-herbst-kuerbis', config.saison.hokkaido);
        return;
      }
      const pumpkin = motif(left, 'button', 'bt-herbst-kuerbis bt-herbst-kuerbis--halloween', config.saison.halloween,
        german ? 'Halloween-Kürbis aufleuchten lassen' : 'Light up the Halloween pumpkin');
      pumpkin.appendChild(picture(config.saison.hell, 'bt-herbst-saison__bild bt-herbst-kuerbis__licht'));
      motif(left, 'span', 'bt-herbst-netz', config.saison.netz);
      motif(left, 'button', 'bt-herbst-spinne', config.saison.spinne,
        german ? 'Spinne wackeln lassen' : 'Make the spider wiggle');
    }

    strip.addEventListener('click', (event) => {
      if (reducedMotion || isExpired()) return;
      const button = event.target.closest('button[data-bt-saison]');
      if (!button || !strip.classList.contains('ist-gewachsen')) return;
      const spider = button.classList.contains('bt-herbst-spinne');
      const activeClass = spider ? 'ist-wackelnd' : 'ist-hell';
      button.classList.remove(activeClass);
      void button.offsetWidth;
      button.classList.add(activeClass);
      setTimeout(() => button.classList.remove(activeClass), WIGGLE_MS);
      if (!spider && config.wind) {
        const rect = button.getBoundingClientRect();
        images.then((loaded) => blow(shuffle(loaded), { x: rect.left + rect.width / 2, y: rect.top }, SECRET_LEAF_COUNT));
      }
    });

    syncSeason();
    // Auch ein über Mitternacht geöffnetes Fenster schaltet ohne Handgriff um.
    if (previousSeason !== 'aus') {
      timer = setInterval(syncSeason, 60000);
      document.addEventListener('visibilitychange', syncSeason);
      window.addEventListener('pageshow', syncSeason);
    }
  }

  function init() {
    if (isExpired()) {
      document.querySelector('.bt-herbst-pilze')?.remove();
      return;
    }

    growMushrooms();

    const blowNow = shouldBlow();
    if (!blowNow && !document.querySelector('.bt-herbst-pilze')) return;

    const images = loadImages(shuffle(config.blaetter));
    enableMushroomSecret(images);
    enableSeason(images);

    if (blowNow) {
      images.then((loaded) => setTimeout(() => blow(loaded), START_DELAY_MS));
    }
  }

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init, { once: true });
  }
})();
